import { Label } from 'cpool-components/general/Label';
import { CurrencyInput } from 'cpool-components/input/CurrencyInput';
import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import { LOAN_REQUEST_CAR_FINANCING_WISHES, LOAN_REQUEST_CAR_TYPES } from 'cpool-constants';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { SchemaCreditRequestForm } from '../../schemas/creditRequestForm.schema';

export const CarInformationForm = () => {
    const { t } = useTranslation();

    const { register, watch } = useFormContext<SchemaCreditRequestForm>();

    // NOTE: unfortunately we need to register the fields here, because the ref is not usable in our input components (except the TextInput component)
    // also the onChange returns a single value (except for the TextInput component) so we cannot pass the value to the onChange function as is
    const registerVehiclePrice = register('carFinance.vehiclePrice');
    const registerDownPayment = register('carFinance.downPayment');
    const registerVehicleType = register('carFinance.vehicleType');
    const registerFinancingWish = register('carFinance.financingWish');

    // NOTE: as mentioned above we need to watch the values here since we cannot use the ref in some input components
    const vehiclePrice = watch('carFinance.vehiclePrice');
    const downPayment = watch('carFinance.downPayment');
    const vehicleType = watch('carFinance.vehicleType');
    const financingWish = watch('carFinance.financingWish');

    return (
        <div className="flex flex-col gap-2 pt-3">
            <div className="text-sm text-midnight font-bold py-1.5">
                {t('BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.CAR_FINANCE.TITLE')}
            </div>
            <div className="flex gap-1">
                <Label htmlFor={registerVehiclePrice.name} className="w-[172px] justify-start text-midnight">
                    {t('BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.CAR_FINANCE.FORM_INPUT_LABELS.VEHICLE_PRICE')}
                </Label>
                <CurrencyInput
                    id={registerVehiclePrice.name}
                    name={registerVehiclePrice.name}
                    className="w-full"
                    onChange={(value) =>
                        registerVehiclePrice.onChange({
                            target: { name: registerVehiclePrice.name, value: value ?? undefined },
                        })
                    }
                    value={vehiclePrice}
                />
            </div>
            <div className="flex gap-1 justify-between">
                <Label htmlFor={registerDownPayment.name} className="w-[172px] justify-start text-midnight">
                    {t('BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.CAR_FINANCE.FORM_INPUT_LABELS.DOWN_PAYMENT')}
                </Label>
                <CurrencyInput
                    id={registerDownPayment.name}
                    name={registerDownPayment.name}
                    className="w-full"
                    onChange={(value) =>
                        registerDownPayment.onChange({
                            target: { name: registerDownPayment.name, value: value ?? undefined },
                        })
                    }
                    value={downPayment}
                />
            </div>
            <div className="flex gap-1 justify-between">
                <Label htmlFor={registerVehicleType.name} className="w-[172px] justify-start text-midnight">
                    {t('BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.CAR_FINANCE.FORM_INPUT_LABELS.VEHICLE_TYPE')}
                </Label>
                <DropdownMenu
                    id={registerVehicleType.name}
                    options={Object.values(LOAN_REQUEST_CAR_TYPES).map((value) => ({
                        label: t(`LOAN_REQUEST_CAR_TYPES.${value}`),
                        value,
                    }))}
                    className="w-full"
                    triggerClassName="w-full"
                    onChange={(value) =>
                        registerVehicleType.onChange({
                            target: { name: registerVehicleType.name, value: value ?? undefined },
                        })
                    }
                    value={vehicleType}
                ></DropdownMenu>
            </div>
            <div className="flex gap-1 justify-between">
                <Label htmlFor={registerFinancingWish.name} className="w-[172px] justify-start text-midnight">{`${t(
                    'BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.CAR_FINANCE.FORM_INPUT_LABELS.FINANCING_WISH',
                )}`}</Label>
                <DropdownMenu
                    id={registerFinancingWish.name}
                    options={Object.values(LOAN_REQUEST_CAR_FINANCING_WISHES).map((value) => ({
                        label: (
                            <div className="flex flex-row gap-1 text-ellipsis overflow-hidden">
                                <div className="font-medium">{t(`LOAN_REQUEST_CAR_FINANCING_WISHES.${value}`)}</div>
                                {t(
                                    `BANK_CREDIT_REQUEST.REQUEST_CONFIGURATION.CAR_FINANCE.FINANCING_WISHES_ADDITIONAL_INFO.${value}`,
                                )}
                            </div>
                        ),
                        value,
                    }))}
                    className="w-full"
                    triggerClassName="w-full"
                    onChange={(value) =>
                        registerFinancingWish.onChange({
                            target: { name: registerFinancingWish.name, value: value ?? undefined },
                        })
                    }
                    value={financingWish}
                />
            </div>
        </div>
    );
};
